"use client";

import { Container, Title, Group, Button, Stack, Text, Box, Badge, Card, Paper } from "@mantine/core";
import { NavigationProgress, setNavigationProgress } from "@mantine/nprogress";
import { IconArrowRight, IconRefresh, IconExternalLink } from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import TypeformQuiz, { QuizQuestion } from "@/components/TypeformQuiz";
import { trackQuizStart, trackQuizComplete, trackCtaClick } from "@/lib/analytics";

function getQuizCount(): number {
  const baseCount = 52147;
  const baseDate = new Date("2025-01-01").getTime();
  const now = Date.now();
  const daysSinceBase = Math.floor((now - baseDate) / (1000 * 60 * 60 * 24));
  return baseCount + daysSinceBase * 89;
}

interface ResultDetail {
  label: string;
  description: string;
  recommendations: string[];
  keyElements: string[];
}

type AestheticType = keyof typeof aestheticQuotes;

const aestheticQuotes = {
  clean_girl: "Polish and simplicity are your signature.",
  cottagecore: "You romanticize life, one cup of tea at a time.",
  baddie: "You were born to stand out — and you know it.",
  dark_academia: "Your mind is your most stylish accessory.",
  light_academia: "Soft light, soft words, soft style.",
  y2k: "Retro meets now — with sparkle.",
  boho: "Freedom looks good on you.",
  e_girl: "Edge and expression in every frame.",
  preppy: "You keep it classy with a twist.",
  vintage: "Timeless style with a personal twist.",
  minimalist: "Less really is more.",
  maximalist: "Your style speaks volumes — all at once.",
  soft_girl: "Sweet, soft, and effortlessly charming.",
  indie_sleaze: "Messy is a mood, and you own it.",
  sporty_chic: "Comfort and confidence in motion.",
};

const questions: QuizQuestion[] = [
  {
    id: "vibe",
    title: "Vibe Check",
    description: "Which overall vibe resonates with you the most?",
    options: [
      { label: "Clean & Polished", value: "clean_girl" },
      { label: "Romantic & Dreamy", value: "cottagecore" },
      { label: "Bold & Confident", value: "baddie" },
      { label: "Intellectual & Mysterious", value: "dark_academia" },
      { label: "Intellectual & Soft", value: "light_academia" },
      { label: "Playful & Nostalgic", value: "y2k" },
      { label: "Relaxed & Natural", value: "boho" },
      { label: "Bold & Internet-Inspired", value: "e_girl" },
    ],
  },
  {
    id: "colors",
    title: "Color Story",
    description: "Which color palette speaks to you?",
    options: [
      { label: "Neutral & Minimal", value: "clean_girl" },
      { label: "Soft & Pastel", value: "soft_girl" },
      { label: "Rich & Moody", value: "dark_academia" },
      { label: "Soft & Muted", value: "light_academia" },
      { label: "Bright & Playful", value: "y2k" },
      { label: "Earthy & Warm", value: "cottagecore" },
      { label: "Bold & Contrasting", value: "baddie" },
      { label: "Black & Neon", value: "e_girl" },
    ],
  },
  {
    id: "elements",
    title: "Style Elements",
    description: "Which style elements do you gravitate towards?",
    options: [
      { label: "Minimal Basics & Tailoring", value: "clean_girl" },
      { label: "Vintage & Thrifted Pieces", value: "y2k" },
      { label: "Academic & Preppy", value: "dark_academia" },
      { label: "Soft Academic & Romantic", value: "light_academia" },
      { label: "Flowy & Feminine", value: "cottagecore" },
      { label: "Edgy & Alternative", value: "e_girl" },
      { label: "Simple & Essential", value: "minimalist" },
      { label: "Bold & Layered", value: "maximalist" },
    ],
  },
  {
    id: "accessories",
    title: "Accessory Game",
    description: "How do you like to accessorize?",
    options: [
      { label: "Minimal & Gold", value: "clean_girl" },
      { label: "Vintage & Unique", value: "y2k" },
      { label: "Academic & Classic", value: "dark_academia" },
      { label: "Nature-Inspired & Delicate", value: "cottagecore" },
      { label: "Bold & Statement", value: "baddie" },
      { label: "Mixed & Eclectic", value: "boho" },
      { label: "Chains & Chokers", value: "e_girl" },
      { label: "Essential & Simple", value: "minimalist" },
    ],
  },
  {
    id: "lifestyle",
    title: "Lifestyle Match",
    description: "Which lifestyle aesthetic feels most authentic to you?",
    options: [
      { label: "Polished & Put-Together", value: "clean_girl" },
      { label: "Academic & Intellectual", value: "dark_academia" },
      { label: "Romantic & Literary", value: "light_academia" },
      { label: "Free-Spirited & Natural", value: "boho" },
      { label: "Trendy & Confident", value: "baddie" },
      { label: "Nostalgic & Playful", value: "y2k" },
      { label: "Internet & Expressive", value: "e_girl" },
      { label: "Simple & Intentional", value: "minimalist" },
    ],
  },
  {
    id: "weekend",
    title: "Weekend Vibes",
    description: "It's a chill weekend. What's your ideal activity?",
    options: [
      { label: "Farmer's market & journaling", value: "cottagecore" },
      { label: "Museum or used bookstore visit", value: "dark_academia" },
      { label: "Dance party or concert", value: "y2k" },
      { label: "Pilates and iced coffee", value: "clean_girl" },
      { label: "Thrift shopping adventure", value: "vintage" },
      { label: "Making TikToks at home", value: "e_girl" },
      { label: "Beach day with friends", value: "boho" },
      { label: "Minimalist home organization", value: "minimalist" },
    ],
  },
];

const resultDetails: Record<AestheticType, ResultDetail> = {
  clean_girl: {
    label: "Clean Girl Aesthetic",
    description: "You embody the modern, minimal, and effortlessly polished aesthetic that's all about quality basics and timeless style.",
    recommendations: ["Invest in high-quality basics in neutral colors", "Focus on clean lines and simple silhouettes", "Accessorize with minimal gold jewelry"],
    keyElements: ["Neutral color palette", "Minimal makeup", "Sleek hairstyles", "Quality basics", "Gold jewelry"],
  },
  cottagecore: {
    label: "Cottagecore Aesthetic",
    description: "You're drawn to romantic, nature-inspired elements and a dreamy, pastoral aesthetic that celebrates simplicity and natural beauty.",
    recommendations: ["Incorporate floral prints and natural fabrics", "Choose soft, flowing silhouettes", "Accessorize with vintage-inspired pieces"],
    keyElements: ["Floral patterns", "Soft colors", "Vintage-inspired pieces", "Natural fabrics", "Delicate accessories"],
  },
  dark_academia: {
    label: "Dark Academia Aesthetic",
    description: "You embody an intellectual, sophisticated style that draws inspiration from classic literature, art, and academic traditions.",
    recommendations: ["Build a wardrobe of classic, tailored pieces", "Incorporate rich, dark colors", "Choose quality fabrics like wool and tweed"],
    keyElements: ["Rich, dark colors", "Classic tailoring", "Vintage-inspired pieces", "Academic elements", "Layered looks"],
  },
  y2k: {
    label: "Y2K Aesthetic",
    description: "You embrace the playful, nostalgic style of the early 2000s, mixing retro elements with modern trends.",
    recommendations: ["Experiment with bold colors and patterns", "Mix vintage pieces with modern items", "Don't be afraid of playful accessories"],
    keyElements: ["Bold colors", "Playful patterns", "Vintage pieces", "Statement accessories", "Mixed textures"],
  },
  baddie: {
    label: "Baddie Aesthetic",
    description: "You rock a confident, bold style that's all about making a statement and owning your look.",
    recommendations: ["Choose bold, statement pieces", "Experiment with trendy items", "Accessorize with eye-catching pieces"],
    keyElements: ["Bold colors", "Statement pieces", "Trendy items", "Eye-catching accessories", "Confident styling"],
  },
  boho: {
    label: "Boho Aesthetic",
    description: "You embody a free-spirited, eclectic style that celebrates individuality and natural elements.",
    recommendations: ["Mix different patterns and textures", "Choose natural, flowing fabrics", "Accessorize with unique pieces"],
    keyElements: ["Mixed patterns", "Natural fabrics", "Unique accessories", "Earthy colors", "Layered looks"],
  },
  light_academia: {
    label: "Light Academia Aesthetic",
    description: "You're drawn to soft, intellectual, and nostalgic vibes — embracing literature, romanticism, and a lighter color palette.",
    recommendations: ["Choose soft neutrals like beige, cream, and light brown", "Incorporate cardigans, trousers, and button-downs", "Layer with scarves and vintage accessories"],
    keyElements: ["Soft neutral colors", "Academic-inspired pieces", "Vintage accessories", "Layered textures", "Romantic styling"],
  },
  e_girl: {
    label: "E-Girl Aesthetic",
    description: "You embody a bold, internet-native style defined by contrast, attitude, and expressive accessories.",
    recommendations: ["Experiment with mesh, chains, and chokers", "Wear heavy eyeliner and bold makeup", "Incorporate oversized tees and mini skirts"],
    keyElements: ["Black and neon colors", "Bold makeup", "Mesh and layered pieces", "Chains and chokers", "Internet-inspired styling"],
  },
  preppy: {
    label: "Preppy Aesthetic",
    description: "You love a clean-cut, sporty, and traditionally academic look — structured, polished, and Ivy League inspired.",
    recommendations: ["Wear collared shirts, blazers, and tennis skirts", "Use classic patterns like plaid and argyle", "Accessorize with headbands and loafers"],
    keyElements: ["Structured clothing", "Plaid and argyle patterns", "Collared tops", "Sporty elegance", "Minimal accessories"],
  },
  vintage: {
    label: "Vintage Aesthetic",
    description: "You're inspired by fashion from decades past — mixing timeless elements with personal flair.",
    recommendations: ["Pick a favorite decade and build a wardrobe around it", "Thrift or find retro-inspired modern pieces", "Use vintage silhouettes"],
    keyElements: ["Retro silhouettes", "Thrifted finds", "Era-specific patterns", "Timeless accessories", "Mixed-decade styling"],
  },
  minimalist: {
    label: "Minimalist Aesthetic",
    description: "You value simplicity, clean lines, and timeless functionality in your style choices.",
    recommendations: ["Stick to a muted color palette", "Avoid cluttered patterns or embellishments", "Choose quality fabrics and construction"],
    keyElements: ["Neutral tones", "Simple silhouettes", "Functional fashion", "Minimal accessories", "Timeless appeal"],
  },
  maximalist: {
    label: "Maximalist Aesthetic",
    description: "You embrace fashion as a form of self-expression — bold colors, patterns, and fearless combinations are your thing.",
    recommendations: ["Mix prints, colors, and textures without fear", "Layer with abandon — more is more", "Use fashion to express mood and personality"],
    keyElements: ["Bold colors", "Pattern mixing", "Layered looks", "Statement accessories", "Eclectic energy"],
  },
  soft_girl: {
    label: "Soft Girl Aesthetic",
    description: "You're drawn to sweet, pastel aesthetics with a gentle, romantic vibe rooted in femininity and nostalgia.",
    recommendations: ["Use pastel colors like baby pink, lavender, and mint", "Opt for cozy cardigans and pleated skirts", "Accessorize with cute clips and blush-toned makeup"],
    keyElements: ["Pastel palette", "Girly silhouettes", "Blush makeup", "Cozy textures", "Whimsical accessories"],
  },
  indie_sleaze: {
    label: "Indie Sleaze Aesthetic",
    description: "You channel grungy, 2000s-inspired rebellion with messy layers, dark eyeliner, and thrift-store energy.",
    recommendations: ["Wear ripped tights, band tees, and unkempt glam", "Lean into chaotic layering and unfiltered style", "Embrace nightlife-inspired, retro-hipster elements"],
    keyElements: ["Grungy textures", "Retro hipster pieces", "Messy glam makeup", "Ripped elements", "Night-out vibe"],
  },
  sporty_chic: {
    label: "Sporty Chic Aesthetic",
    description: "You combine athletic comfort with stylish touches — think tennis skirts, crop tops, and varsity jackets.",
    recommendations: ["Start with sporty basics like bike shorts or joggers", "Layer with chic jackets or sleek accessories", "Add sneakers and minimal jewelry"],
    keyElements: ["Athleisure base", "Sporty-meets-stylish pieces", "Comfortable fabrics", "Caps and sneakers", "Neutral palettes"],
  },
};

function determineAesthetic(answers: Record<string, string>): AestheticType {
  const answerCounts = Object.values(answers).reduce((acc, value) => {
    acc[value as AestheticType] = (acc[value as AestheticType] || 0) + 1;
    return acc;
  }, {} as Record<AestheticType, number>);

  const sortedAnswers = Object.entries(answerCounts).sort((a, b) => b[1] - a[1]);
  return (sortedAnswers[0]?.[0] || "clean_girl") as AestheticType;
}

export default function AestheticQuiz() {
  const [quizState, setQuizState] = useState<"intro" | "quiz" | "results">("intro");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showStickyButton, setShowStickyButton] = useState(false);
  const mainCtaRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (quizState !== "intro") return;

    const handleScroll = () => {
      if (mainCtaRef.current) {
        const rect = mainCtaRef.current.getBoundingClientRect();
        setShowStickyButton(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [quizState]);

  const startQuiz = () => {
    setQuizState("quiz");
    setNavigationProgress(0);
    trackQuizStart("Aesthetic Quiz");
  };

  const handleQuizComplete = (quizAnswers: Record<string, string>) => {
    setAnswers(quizAnswers);
    setQuizState("results");
    setNavigationProgress(100);
    trackQuizComplete("Aesthetic Quiz");
  };

  const handleQuizExit = () => {
    setQuizState("intro");
    setNavigationProgress(0);
  };

  const resetQuiz = () => {
    setQuizState("intro");
    setAnswers({});
    setNavigationProgress(0);
  };

  // ============================================
  // RESULTS VIEW
  // ============================================
  if (quizState === "results") {
    const aestheticType = determineAesthetic(answers);
    const result = resultDetails[aestheticType];
    const quote = aestheticQuotes[aestheticType];

    return (
      <Container size="md" py="xl">
        <NavigationProgress size={4} color="wumpus" />

        <Stack gap="lg" align="center">
          <Card withBorder p="xl" radius="md" w="100%">
            <Stack gap="lg" ta="center" align="center">
              <Badge size="lg" color="violet" variant="light">
                Your Result
              </Badge>
              <Title order={2} c="violet.7">
                {result.label}
              </Title>
              <Text size="sm" c="dimmed" fs="italic">
                {quote}
              </Text>
              <Group gap="xs" justify="center" wrap="wrap">
                {result.keyElements.slice(0, 4).map((element, index) => (
                  <Badge key={index} variant="outline" color="gray" size="sm">
                    {element}
                  </Badge>
                ))}
              </Group>

              <QuizPhotoUpload quizName="Aesthetic Quiz" />

              <Group gap="xs" justify="center">
                <Button
                  component={Link}
                  href={`/aesthetic/${aestheticType.replace(/_/g, "-")}`}
                  variant="subtle"
                  color="gray"
                  size="sm"
                >
                  Explore {result.label} →
                </Button>
                <Button component={Link} href="/aesthetic" variant="subtle" color="gray" size="sm">
                  See all aesthetics →
                </Button>
              </Group>

              <Card withBorder p="lg" radius="md" w="100%">
                <Stack gap="sm" ta="center" align="center">
                  <Title order={3}>Every aesthetic has a palette</Title>
                  <Text size="sm" c="dimmed">
                    See the exact colors that make your aesthetic work on you — AI-draped on your photo.
                  </Text>
                  <Button
                    component={Link}
                    href="/ai-color-analysis?src=bridge_aesthetic"
                    color="violet"
                    onClick={() => trackCtaClick("bridge_aesthetic_ca")}
                  >
                    Get my palette
                  </Button>
                </Stack>
              </Card>

              <Box w="100%">
                <EmailResultsCapture
                  source="aesthetic-quiz"
                  resultLabel={result.label}
                  title={`Email me my ${result.label} style guide`}
                  subtitle="Your aesthetic breakdown plus the colors that make it work — free. Unsubscribe anytime."
                />
              </Box>
            </Stack>
          </Card>

          <Button variant="subtle" onClick={resetQuiz} leftSection={<IconRefresh size="1rem" />} color="gray" size="sm">
            Take Quiz Again
          </Button>
        </Stack>
      </Container>
    );
  }

  // ============================================
  // QUIZ VIEW (Typeform-style)
  // ============================================
  if (quizState === "quiz") {
    return (
      <>
        <NavigationProgress size={4} color="wumpus" />
        <TypeformQuiz
          questions={questions}
          onComplete={handleQuizComplete}
          onExit={handleQuizExit}
          accentColor="#7C3AED"
        />
      </>
    );
  }

  // ============================================
  // INTRO VIEW
  // ============================================
  return (
    <>
      <Container size="md" py="xl">
        <NavigationProgress size={4} color="wumpus" />

        <Stack gap="md" ta="center" maw={500} mx="auto">
          <Title order={1}>What Is My Aesthetic? Free Aesthetic Quiz</Title>

          <Text c="dimmed">6 questions · Takes 1 minute</Text>

          <Button
            ref={mainCtaRef}
            size="xl"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1.2rem" />}
            color="wumpus"
            fullWidth
          >
            Start Free Aesthetic Quiz
          </Button>

          <Text size="xs" c="dimmed">
            No signup required · {getQuizCount().toLocaleString()} people have taken this quiz
          </Text>
        </Stack>

        <Stack gap="md" mt="xl">
          <Text>
            This quiz will help you discover your personal aesthetic — the visual style that best expresses who you are.
          </Text>
          <Box>
            <Text mb="xs">By knowing your aesthetic, you can:</Text>
            <Stack gap="xs" ml="md">
              <Text>• Make confident style choices that feel authentic</Text>
              <Text>• Build a cohesive wardrobe that works together</Text>
              <Text>• Find your style community and inspiration online</Text>
              <Text>• Stop buying clothes that don't fit your vibe</Text>
            </Stack>
          </Box>
        </Stack>

        <Stack gap="xl" mt="xl">
          <Box>
            <Title order={2} size="h3" mb="sm">What is an aesthetic?</Title>
            <Text c="dimmed">
              Your aesthetic is your personal style identity — the visual expression of your personality through fashion, decor, and lifestyle choices.
            </Text>
          </Box>

          <Box ta="center" py="md">
            <Button
              size="lg"
              onClick={startQuiz}
              rightSection={<IconArrowRight size="1rem" />}
              color="wumpus"
              variant="light"
            >
              Take the Quiz Now
            </Button>
          </Box>

          <Box>
            <Title order={2} size="h3" mb="sm">Types of aesthetics in this quiz</Title>
            <Text c="dimmed">
              This aesthetic quiz covers 15+ modern style aesthetics including Clean Girl, Dark Academia, Cottagecore, Y2K, Minimalist, and more.
            </Text>
          </Box>
        </Stack>
      </Container>

      {showStickyButton && (
        <Box
          hiddenFrom="md"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "0.75rem 1rem",
            background: "white",
            borderTop: "1px solid #eee",
            zIndex: 100,
          }}
        >
          <Button
            size="md"
            onClick={startQuiz}
            rightSection={<IconArrowRight size="1rem" />}
            color="wumpus"
            fullWidth
          >
            Start Free Aesthetic Quiz
          </Button>
        </Box>
      )}
    </>
  );
}
